import React, { useEffect, type FC, useRef } from 'react';
import './ListPagination.scss';
import { useTranslation } from 'react-i18next';

/**
 * @description Props for the ListPagination component
 * @typedef {Object} ListPaginationProps
 * @property {Function} setPageIndex - Function to set the current page index
 * @property {Function} setPageSize - Function to set the number of items per page
 * @property {number} pageIndex - Current page index (zero-based)
 * @property {Function} previousPage - Function to navigate to the previous page
 * @property {number} pageCount - Total number of pages
 * @property {number} pageSize - Number of items displayed per page
 * @property {Function} nextPage - Function to navigate to the next page
 * @property {boolean} canGetPreviousPage - Whether the previous page is available
 * @property {boolean} canGetNextPage - Whether the next page is available
 * @property {boolean} [hidePageSize=false] - Whether to hide the page size selector
 * @property {boolean} [responsive=false] - Whether to use responsive styling
 */
type ListPaginationProps = {
  setPageIndex: Function;
  setPageSize: Function;
  pageIndex: number;
  previousPage: Function;
  pageCount: number;
  pageSize: number;
  nextPage: Function;
  canGetPreviousPage: boolean;
  canGetNextPage: boolean;
  hidePageSize?: boolean;
  responsive?: boolean;
};

/**
 * @description Pagination component for lists and tables.
 * Provides navigation controls for paginated data including previous/next page buttons,
 * current page indicator with direct page input, and page size selector.
 *
 * @SADtrace SAD-003-3.1, SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {ListPaginationProps} props - Component props
 *
 * @returns {JSX.Element} Rendered pagination component
 */
const ListPagination: FC<ListPaginationProps> = ({
  setPageIndex,
  setPageSize,
  pageIndex,
  canGetPreviousPage,
  previousPage,
  pageCount,
  pageSize,
  canGetNextPage,
  nextPage,
  hidePageSize = false,
  responsive = false,
}) => {
  const { t } = useTranslation();

  const inputValue = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputValue.current) {
      inputValue.current.value = (pageIndex + 1).toString();
    }
  }, [pageIndex]);

  if (pageIndex < 0) {
    pageIndex = 0;
  }

  /**
   * @description Prevents entering minus, period, or comma characters in the page input
   *
   * @SADtrace SAD-003-3.1, SAD-003-4.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} event - Keyboard event
   *
   * @returns {void}
   */
  const preventMinus = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Minus', 'Period', 'Comma'].includes(event.code)) {
      event.preventDefault();
    }
  };

  /**
   * @description Updates the current page based on user input.
   * Validates page number and resets to current page if invalid.
   *
   * @SADtrace SAD-003-3.1, SAD-003-4.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>} event - Input event
   *
   * @returns {void}
   */
  const modifyTablePageState = (
    event:
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // v8 ignore start -- WAIVER(DEFENSIVE): keyboard and blur events originate from the bound page input, so the target is always an HTMLInputElement; guard retained as type-narrowing fail-safe
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    // v8 ignore stop -- WAIVER(DEFENSIVE)

    const page = Number(event.target.value) - 1;

    if (page >= pageCount) {
      // v8 ignore start -- WAIVER(DEFENSIVE): the ref is bound to the rendered page input, so it is never null while this handler can fire; null side kept as a fail-safe
      if (inputValue.current) {
        // v8 ignore stop -- WAIVER(DEFENSIVE)
        inputValue.current.value = (pageIndex + 1).toString();
      }

      return;
    }

    //if page is the same as the current page, do nothing
    if (pageIndex === page) return;

    //if blank reset input to defaultValue
    if ((event.target.value === '' || page < 0) && inputValue.current)
      return (inputValue.current.value = (pageIndex + 1).toString());

    // v8 ignore start -- WAIVER(DEFENSIVE): false arm unreachable — blank input and negative pages already returned through the preceding guard, so page is always >= 0 here
    if (page >= 0) {
      // v8 ignore stop -- WAIVER(DEFENSIVE)
      setPageIndex(page);
    }
  };

  /**
   * @description Handles Enter key press in the page input field.
   * Triggers page update when Enter is pressed.
   *
   * @SADtrace SAD-003-3.1, SAD-003-4.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} event - Keyboard event
   *
   * @returns {void}
   */
  const handlePageEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      modifyTablePageState(event);
    }
  };

  return (
    <div
      className="d-flex justify-content-between dl-list-pagination flex-nowrap"
      data-testid="list-pagination-test-id"
    >
      <div className={'dl-list-pagination__previous'}>
        <button
          onClick={() => previousPage()}
          disabled={!canGetPreviousPage}
          className={`${responsive ? 'dl-list-pagination__responsive' : ''}`}
        >
          {t('pagination.previous')}
        </button>
      </div>

      <div className="dl-list-pagination__center">
        {pageIndex >= 0 && pageCount > 0 && (
          <span
            className={`dl-list-pagination__page-info ${responsive ? 'dl-list-pagination__responsive' : ''}`}
          >
            {responsive
              ? t('pagination.responsive.page')
              : t('pagination.go_to')}
            <div className="dl-list-pagination__page-jump">
              <input
                className={responsive ? 'dl-list-pagination__responsive' : ''}
                aria-label="jump to page"
                type="number"
                min={1}
                ref={inputValue}
                defaultValue={pageIndex + 1}
                onKeyDown={preventMinus}
                onKeyUp={handlePageEnter}
                onBlur={modifyTablePageState}
              />
            </div>
            <span className="dl-list-pagination__total-pages">
              {hidePageSize
                ? t('pagination.responsive.of') + pageCount
                : ' | ' +
                  t('pagination.page_number', {
                    value: pageIndex + 1,
                    total: pageCount,
                  })}
            </span>
          </span>
        )}
        {pageIndex >= 0 && pageCount > 0 && !hidePageSize && (
          <span className="dl-list-pagination__select-wrap">
            <select
              aria-label="rows per page"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageIndex(0);
              }}
            >
              {[2, 10, 20, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize} {t('pagination.rows')}
                </option>
              ))}
            </select>
          </span>
        )}
      </div>

      <div className="dl-list-pagination__next">
        <button
          onClick={() => nextPage()}
          disabled={!canGetNextPage}
          className={`${responsive ? 'dl-list-pagination__responsive' : ''}`}
        >
          {t('pagination.next')}
        </button>
      </div>
    </div>
  );
};

export default ListPagination;
